import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../../style/examinee/questionnaire-test-page.module.css';

const DEBOUNCE_MS = 500;

const FormField = ({ label, value }) => (
  <div className={styles.formField}>
    <span className={styles.formFieldLabel}>{label}</span>
    <span className={styles.formFieldValue}>{value}</span>
  </div>
);

const QuestionnaireHeader = ({
  headerData,
  onParentNameChange,
  parentNameRequired,
  hideParentNameField,
  resumedParentName,
  readOnly = false
}) => {
  const [parentName, setParentName] = useState('');
  const timerRef = useRef(null);

  // Initialize from a name already saved to the record (a resumed questionnaire,
  // PIN-221), else the raw respondentName (not parentName, which falls back to
  // examiner name)
  useEffect(() => {
    const initialName = resumedParentName || headerData?.formFields?.respondentName;
    if (initialName) {
      setParentName(initialName);
    }
  }, [headerData, resumedParentName]);

  const debouncedSave = useCallback(
    (value) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (onParentNameChange) {
          onParentNameChange(value);
        }
      }, DEBOUNCE_MS);
    },
    [onParentNameChange]
  );

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleParentNameChange = useCallback(
    (e) => {
      const value = e.target.value;
      setParentName(value);
      debouncedSave(value);
    },
    [debouncedSave]
  );

  // Flush pending debounce on blur so the value is saved before submit
  const handleBlur = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      if (onParentNameChange) {
        onParentNameChange(parentName);
      }
    }
  }, [onParentNameChange, parentName]);

  if (!headerData) {
    return (
      <div className={styles.header}>
        <h1 className={styles.title}>Questionnaire</h1>
      </div>
    );
  }

  const {
    title,
    subtitle,
    copyright,
    directions,
    directionsBullets,
    directionsFooter,
    formFields
  } = headerData;
  // Directions lines the form wants called out individually rather than run
  // together in the paragraph — STARS lists its four response options this way
  // (PIN-179 #1). Other questionnaires send none and render as before.
  const bullets = directionsBullets || [];

  return (
    <div className={styles.questionnaireHeader}>
      <h1 className={styles.headerTitle}>{title}</h1>
      <h2 className={styles.headerSubtitle}>{subtitle}</h2>
      <p className={styles.copyright}>{copyright}</p>

      <div className={styles.formFieldsGrid}>
        <FormField label="Name:" value={formFields.childName} />
        <FormField label="Gender:" value={formFields.childGender} />
        <FormField label="Date of Birth:" value={formFields.dateOfBirth} />
        <FormField label="Grade:" value={formFields.grade} />
        {!hideParentNameField && readOnly && (
          <FormField
            label={`${headerData.raterLabel ?? 'Form Completed By'}:`}
            value={parentName}
          />
        )}
        {!hideParentNameField && !readOnly && (
          <div className={styles.formField}>
            <label className={styles.formFieldLabel} htmlFor="parentName">
              {headerData.raterLabel ?? 'Form Completed By'}:
              {parentNameRequired && !parentName.trim() && (
                <span className={styles.requiredIndicator}> (Required)</span>
              )}
            </label>
            <input
              id="parentName"
              type="text"
              className={styles.formFieldInput}
              value={parentName}
              onChange={handleParentNameChange}
              onBlur={handleBlur}
              placeholder="Enter your name"
              data-testid="questionnaire-parent-name"
            />
          </div>
        )}
        <FormField label="Today's Date:" value={formFields.todayDate} />
      </div>

      <div className={styles.directions}>
        <strong>Directions:</strong> <span>{directions}</span>
        {bullets.length > 0 && (
          <ul className={styles.directionsBullets} data-testid="questionnaire-directions-bullets">
            {bullets.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
        {directionsFooter && (
          <p data-testid="questionnaire-directions-footer">{directionsFooter}</p>
        )}
      </div>
    </div>
  );
};

export default QuestionnaireHeader;
